/**
 * MessageList — unified message renderer for both 1:1 interviews and focus groups.
 *
 * Mode is auto-detected from the participants array:
 *   - 1 participant  → interview mode (no sender names, grouped consecutive bubbles)
 *   - 2+ participants → group mode (avatar + sender name per twin message)
 *
 * Delegates all bubble rendering to the shared ChatBubble component.
 *
 * Group mode also renders the real-room surfaces (Step 11): reaction chips
 * under twin, display and user messages; the Insights assistant's audience
 * chart (`audience_plot`); the document chip on user messages; and one typing
 * row for every pending speaker (twins first, then the assistant), even when alone.
 */

import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText } from '@digital-twins/design-system/icons'
import { Badge } from '@/components/ui/badge'
import { ChatBubble, getBubblePosition } from '@/components/chat/ChatBubble'
import { ChatTypingDots } from '@/components/chat/ChatTypingDots'
import { ScrollableImagePreview } from '@/components/media/ScrollableImagePreview'
import { AttachmentTiles } from '@/components/chat/AttachmentTiles'
import { ReactionChips } from '@/components/chat/ReactionChips/ReactionChips'
import { AudiencePlotMessage, isAudiencePlotMetadata } from '@/components/chat/AudiencePlotMessage/AudiencePlotMessage'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { InsightsAssistantAvatar } from '@/components/chat/InsightsAssistantAvatar/InsightsAssistantAvatar'
import { getUserInitials } from '@/lib/utils/avatar'
import { chatRoomsApi } from '@/lib/api/client'
import { toDisplayMessages } from './displayMessages'
import { AnswerGroundingMeter, readAnswerGrounding } from './AnswerGroundingMeter/AnswerGroundingMeter'
import { ReplyQuote } from './ReplyQuote/ReplyQuote'

import {
  ChatMessageDocumentRef,
  ChatRoomMessageDto,
  ChatRoomParticipantDto,
  ChatRoomReactionDto,
  SampleArtifactMetadata,
  GEORGE_PARTICIPANT_ID,
} from '@digital-twins/shared-types'

/**
 * A user message's attached videos and images, as standalone right-aligned thumbnails.
 * Videos keep their own shape and open full size with controls on click, like every
 * other sent creative. For images, prefers the already-decoded base64 (instant — present on an optimistic send and
 * carried onto the reconciled persisted message) over the server-hosted key,
 * which has to round-trip and otherwise leaves a blank box while it loads.
 */
function UserMessageImages({
  msg,
  roomToken,
  getImageUrl,
}: {
  msg: ChatRoomMessageDto
  roomToken: string | undefined
  getImageUrl: (token: string, key: string) => string
}) {
  // A sent PDF travels as its rendered image, first in the list (Step 11c);
  // the document chip stands for it, so it is not shown twice.
  const documentImageCount = msg.documentRefs?.length ?? 0
  const base64Images = (msg.images ?? []).slice(documentImageCount)
  const imageKeys = (msg.imageKeys ?? []).slice(documentImageCount)
  const hasBase64 = base64Images.length > 0
  const hasKeys = Boolean(roomToken && imageKeys.length > 0)
  const videos = msg.videoRefs ?? []
  if (!hasBase64 && !hasKeys && videos.length === 0) return null
  return (
    <div className="flex flex-wrap justify-end gap-2 mt-1">
      <AttachmentTiles
        items={videos.map((video) => ({
          src: video.url,
          mimeType: video.mimeType ?? 'video/mp4',
          label: video.label,
        }))}
        testIdPrefix="user-message-video"
      />
      {hasBase64
        ? base64Images.map((img, i) => (
            <ScrollableImagePreview
              key={`b64-${i}`}
              src={`data:${img.mimeType};base64,${img.base64}`}
              alt="Attached image"
              thumbnailClassName="h-36 w-36"
            />
          ))
        : hasKeys
          ? imageKeys.map((imgKey) => (
              <ScrollableImagePreview
                key={imgKey}
                src={getImageUrl(roomToken!, imgKey)}
                alt="Attached image"
                thumbnailClassName="h-36 w-36"
              />
            ))
          : null}
    </div>
  )
}

interface MessageListProps {
  messages: Array<ChatRoomMessageDto>
  participants: Array<ChatRoomParticipantDto>
  thinkingParticipantIds?: Array<string>
  /** Accepted for caller compatibility; rooms start empty, so nothing reads it. */
  isRoomActive?: boolean
  emptyStateText?: string
  /** When set, renders a "Guest conversation" divider before the message at this index. */
  guestDividerIndex?: number
  /**
   * Room the messages belong to. Defaults to the `/chats/:id` route param;
   * embedded hosts (Quick Study, guest focus groups) must pass it explicitly.
   */
  roomToken?: string
  /**
   * Builds the URL for a stored image key. Guests resolve images through the
   * public share route, which is why this is injectable rather than hardcoded.
   */
  getImageUrl?: (token: string, key: string) => string
  /** C13: the room's resolved reactions (group mode renders them as chips). */
  reactions?: Array<ChatRoomReactionDto>
  /** Keys of reactions that arrived live; only those chips pop. */
  liveReactionKeys?: ReadonlySet<string>
}

const EMPTY_REACTIONS: Array<ChatRoomReactionDto> = []

/** Delay between messages that land in the same burst (SPEC section 4). */
const BURST_STAGGER_MS = 80

const DOCUMENT_HEADER_RE = /^\[Document: [^\]\n]*\]\n\n/
const DOCUMENT_END_MARKER = '[/Document]'

/**
 * The moderator's own words in a user message that carried PDFs (Step 10c).
 * The composer inlines the document text for the twins; the bubble shows a
 * document chip instead. With the end marker the split is exact; without it
 * only the header line is dropped (the inlined text cannot be told apart).
 */
export function userTextWithoutDocuments(
  content: string,
  documentRefs: Array<ChatMessageDocumentRef> | undefined,
): string {
  if (!documentRefs || documentRefs.length === 0) {
    return content
  }
  const endIndex = content.lastIndexOf(DOCUMENT_END_MARKER)
  if (endIndex !== -1) {
    return content.slice(endIndex + DOCUMENT_END_MARKER.length).trim()
  }
  return content.replace(DOCUMENT_HEADER_RE, '').trim()
}

function DocumentChips(props: { msg: ChatRoomMessageDto }) {
  const { msg } = props
  const documents = msg.documentRefs ?? []

  if (documents.length === 0) {
    return null
  }

  return (
    <div className="mb-1 flex flex-wrap justify-end gap-2" data-testid={`message-document-${msg.id}`}>
      {documents.map((document, index) => (
        <span
          key={`${document.key}-${index}`}
          className="inline-flex max-w-[240px] items-center gap-1.5 rounded-lg border border-twin-chat-divider/80 bg-twin-chat-surface px-3 py-1.5 text-xs text-twin-chat-fg"
        >
          <FileText className="h-3.5 w-3.5 shrink-0 text-twin-chat-fg-muted" aria-hidden="true" />
          <span className="truncate">{document.name}</span>
        </span>
      ))}
    </div>
  )
}

function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

const ENTRANCE_STYLE: React.CSSProperties = {
  animation: 'chat-message-in 0.3s ease-out both',
}

function GuestDivider() {
  return (
    <div className="flex items-center gap-3 py-3" data-testid="guest-divider">
      <div className="flex-1 border-t border-twin-chat-divider/20" />
      <span className="text-xs font-medium text-twin-chat-fg-muted">Guest conversation</span>
      <div className="flex-1 border-t border-twin-chat-divider/20" />
    </div>
  )
}

function isSampleArtifact(
  metadata: SampleArtifactMetadata | Record<string, unknown> | null,
): metadata is SampleArtifactMetadata {
  if (!metadata || typeof metadata !== 'object') return false
  const m = metadata as Record<string, unknown>
  return typeof m.chartType === 'string' && m.chartData != null && Array.isArray(m.keyInsights)
}

function findParticipant(participants: Array<ChatRoomParticipantDto>, id: string | null) {
  if (!id) return null
  return participants.find((p) => p.id === id) ?? null
}

function buildFullTagline(
  rawTagline: string | null,
  audienceTagNames: Array<string>,
): string | null {
  if (audienceTagNames.length > 0 && rawTagline) {
    return `${rawTagline} · ${audienceTagNames.join(' · ')}`
  }
  if (audienceTagNames.length > 0) {
    return audienceTagNames.join(' · ')
  }
  return rawTagline
}

// ---------------------------------------------------------------------------
// Sample artifact inline renderer (reused from the room MessageList)
// ---------------------------------------------------------------------------

function SampleArtifactInline(props: { metadata: SampleArtifactMetadata }) {
  const { metadata } = props

  return (
    <div className="mt-3 space-y-3">
      <div className="bg-twin-chat-surface/50 rounded-lg p-4 border border-twin-chat-divider/30">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="accent" className="text-[10px]">
            {metadata.chartType}
          </Badge>
          <span className="text-xs text-twin-chat-fg-muted">n={metadata.sampleSize}</span>
        </div>

        <div className="space-y-2">
          {metadata.chartData.labels.map((label, i) => {
            const pct = metadata.chartData.percentages[i] ?? 0
            return (
              <div key={label} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-twin-chat-fg">{label}</span>
                  <span className="text-twin-chat-fg-muted font-medium">{pct.toFixed(1)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-twin-chat-bubble/40 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-twin-chat-send transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>

        <p className="text-2xs text-twin-chat-fg-muted mt-3">
          Based on a sample of {metadata.sampleSize} &middot; &quot;
          {metadata.questionUsed}&quot;
        </p>
      </div>

      {metadata.keyInsights.length > 0 && (
        <ul className="space-y-1.5 pl-1">
          {metadata.keyInsights.map((insight, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-twin-chat-fg">
              <span className="text-twin-chat-accent mt-1 shrink-0">&bull;</span>
              <span>{insight}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Thinking dots row (interview mode) — dots only, aligned with message content
// Matches the study wizard's QuestionnaireBuilderChat thinking indicator.
// ---------------------------------------------------------------------------

// Above the longest turn (a new-video turn runs up to 210 s), so the warning never fires on a healthy turn.
const SLOW_THINKING_MS = 240_000

function ThinkingDotsRow({ isThinking }: { isThinking: boolean }) {
  const [showSlowWarning, setShowSlowWarning] = useState(false)

  useEffect(() => {
    if (!isThinking) {
      setShowSlowWarning(false)
      return
    }
    const timer = setTimeout(() => setShowSlowWarning(true), SLOW_THINKING_MS)
    return () => clearTimeout(timer)
  }, [isThinking])

  if (!isThinking) return null

  return (
    <>
      <div
        className="flex items-center gap-3 pl-9 animate-fade-in"
        role="status"
        aria-live="polite"
        data-testid="chat-thinking-indicator"
      >
        <ChatTypingDots />
      </div>
      {showSlowWarning && (
        <div className="mt-1 pl-9 flex items-center gap-2">
          <p className="text-xs text-twin-chat-fg-muted">This is taking longer than usual.</p>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Typing row (group mode) — avatar + dots
// ---------------------------------------------------------------------------

interface TypingSpeaker {
  id: string
  name: string
  imageUrl: string | null
  kind: 'assistant' | 'twin'
}

/** The typing row: stacked avatars, up to three first names, the same dots. */
function TypingGroupRow(props: { twins: Array<TypingSpeaker> }) {
  const { twins } = props

  const { t, i18n } = useTranslation('app')

  const names = twins.map((twin) => (twin.kind === 'assistant' ? twin.name : firstName(twin.name)))
  const shownNames = names.length > 3
    ? t('roomThread.namesOverflow', {
        names: new Intl.ListFormat(i18n.language, { type: 'unit', style: 'short' }).format(names.slice(0, 3)),
        count: names.length - 3,
      })
    : new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(names)

  return (
    <div className="flex items-center gap-2 animate-fade-in" data-testid="chat-typing-group" data-count={twins.length}>
      <div className="flex -space-x-1.5" aria-hidden="true">
        {twins.slice(0, 3).map((twin) => {
          if (twin.kind === 'assistant') {
            return (
              <span key={twin.id} className="rounded-full ring-2 ring-background" data-testid="chat-typing-group-assistant">
                <InsightsAssistantAvatar className="h-7 w-7" testId="typing-row-assistant-icon" />
              </span>
            )
          }
          return twin.imageUrl ? (
            <Avatar key={twin.id} className="h-7 w-7 rounded-full ring-2 ring-background">
              <AvatarImage src={twin.imageUrl} alt="" className="object-cover object-top" />
              <AvatarFallback className="rounded-full p-0">
                <UserAvatar initials={getUserInitials(twin.name)} name={twin.name} size="xs" />
              </AvatarFallback>
            </Avatar>
          ) : (
            <span key={twin.id} className="rounded-full ring-2 ring-background" data-testid="chat-typing-group-initials">
              <UserAvatar initials={getUserInitials(twin.name)} name={twin.name} size="xs" />
            </span>
          )
        })}
      </div>
      <span className="inline-flex items-baseline gap-1 text-xs text-twin-chat-fg-muted" aria-hidden="true">
        {shownNames}
        <ChatTypingDots variant="inline" />
      </span>
      <span className="sr-only" role="status" aria-live="polite">
        {t('roomThread.typingStatus', { names: shownNames, count: twins.length })}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Interview-mode message grouping (consecutive same-sender messages)
// ---------------------------------------------------------------------------

interface MessageGroup {
  senderType: ChatRoomMessageDto['senderType']
  senderParticipantId: string | null
  messages: Array<ChatRoomMessageDto>
}

function groupConsecutiveMessages(messages: Array<ChatRoomMessageDto>): Array<MessageGroup> {
  const groups: Array<MessageGroup> = []
  for (const msg of messages) {
    const last = groups[groups.length - 1]
    if (
      last &&
      last.senderType === msg.senderType &&
      last.senderParticipantId === msg.senderParticipantId
    ) {
      last.messages.push(msg)
    } else {
      groups.push({
        senderType: msg.senderType,
        senderParticipantId: msg.senderParticipantId,
        messages: [msg],
      })
    }
  }
  return groups
}

// ---------------------------------------------------------------------------
// Interview mode renderer (1:1)
// ---------------------------------------------------------------------------

function InterviewMessages(props: {
  messages: Array<ChatRoomMessageDto>
  participant: ChatRoomParticipantDto | null
  thinkingParticipantIds: Array<string>
  newMessageIds: Set<string>
  /** Row keys from toDisplayMessages; a send keeps its key across the optimistic swap. */
  renderKeys: Map<ChatRoomMessageDto, string>
  guestDividerIndex?: number
  roomToken: string | undefined
  getImageUrl: (token: string, key: string) => string
}) {
  const {
    messages,
    participant,
    thinkingParticipantIds,
    newMessageIds,
    renderKeys,
    guestDividerIndex,
    roomToken,
    getImageUrl,
  } = props
  const groups = groupConsecutiveMessages(messages)
  const isThinking = thinkingParticipantIds.length > 0

  let cumulativeIndex = 0

  return (
    <>
      {groups.map((group, gi) => {
        const groupStartIndex = cumulativeIndex
        cumulativeIndex += group.messages.length
        const showDividerBefore =
          guestDividerIndex != null &&
          groupStartIndex >= guestDividerIndex &&
          groupStartIndex === guestDividerIndex

        return (
          <div key={gi}>
            {showDividerBefore && <GuestDivider />}
            <div className="flex flex-col gap-1">
              {group.messages.map((msg, mi) => {
                const rowKey = renderKeys.get(msg) ?? msg.id
                const isNew = newMessageIds.has(rowKey)

                if (msg.senderType === 'system') {
                  return (
                    <div
                      key={rowKey}
                      className="flex justify-center py-2"
                      style={isNew ? ENTRANCE_STYLE : undefined}
                      data-testid={`message-${msg.id}`}
                    >
                      <span className="text-xs text-twin-chat-fg-muted bg-twin-chat-bubble/30 px-3 py-1 rounded-full">
                        {msg.content}
                      </span>
                    </div>
                  )
                }

                const role = msg.senderType === 'user' ? ('user' as const) : ('assistant' as const)
                // The meter sits under the avatar column of every twin answer, including
                // grouped 2nd+ answers (reserved avatar space). Intros carry no level by
                // design; George and users never do.
                const answerGrounding =
                  msg.senderType === 'twin' ? readAnswerGrounding(msg.metadata) : null

                return (
                  <div
                    key={rowKey}
                    style={isNew ? ENTRANCE_STYLE : undefined}
                    data-testid={`message-${msg.id}`}
                  >
                    <ChatBubble
                      role={role}
                      content={msg.content}
                      position={getBubblePosition(mi, group.messages.length)}
                      avatar={
                        role === 'assistant' && participant
                          ? mi === 0
                            ? {
                                url: participant.imageUrl,
                                fallbackInitials: getUserInitials(participant.displayName),
                              }
                            : null
                          : undefined
                      }
                      belowAvatar={
                        answerGrounding ? (
                          <AnswerGroundingMeter messageId={msg.id} level={answerGrounding} />
                        ) : undefined
                      }
                    />
                    {role === 'user' && (
                      <UserMessageImages
                        msg={msg}
                        roomToken={roomToken}
                        getImageUrl={getImageUrl}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {isThinking && participant && <ThinkingDotsRow isThinking={isThinking} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Group mode renderer (focus group)
// ---------------------------------------------------------------------------

function GroupMessages(props: {
  messages: Array<ChatRoomMessageDto>
  participants: Array<ChatRoomParticipantDto>
  thinkingParticipantIds: Array<string>
  newMessageIds: Set<string>
  /** Row keys from toDisplayMessages; a send keeps its key across the optimistic swap. */
  renderKeys: Map<ChatRoomMessageDto, string>
  guestDividerIndex?: number
  roomToken: string | undefined
  getImageUrl: (token: string, key: string) => string
  reactions: Array<ChatRoomReactionDto>
  liveReactionKeys?: ReadonlySet<string>
  entranceDelays: Map<string, number>
}) {
  const {
    messages,
    participants,
    thinkingParticipantIds,
    newMessageIds,
    renderKeys,
    guestDividerIndex,
    roomToken,
    getImageUrl,
    reactions,
    liveReactionKeys,
    entranceDelays,
  } = props

  const { t } = useTranslation('app')
  const assistantName = t('roomThread.assistantName')

  const reactionsBySequence = useMemo(() => {
    const bySequence = new Map<number, Array<ChatRoomReactionDto>>()
    for (const reaction of reactions) {
      const list = bySequence.get(reaction.messageSequenceIndex) ?? []
      list.push(reaction)
      bySequence.set(reaction.messageSequenceIndex, list)
    }
    return bySequence
  }, [reactions])

  const isGeorgeThinking = thinkingParticipantIds.includes(GEORGE_PARTICIPANT_ID)
  const thinkingTwinIds = useMemo(
    () => thinkingParticipantIds.filter((id) => id !== GEORGE_PARTICIPANT_ID),
    [thinkingParticipantIds],
  )

  const entranceStyle = (msg: ChatRoomMessageDto): React.CSSProperties | undefined => {
    const rowKey = renderKeys.get(msg) ?? msg.id
    if (!newMessageIds.has(rowKey)) {
      return undefined
    }
    const delay = entranceDelays.get(rowKey) ?? 0
    return delay > 0 ? { ...ENTRANCE_STYLE, animationDelay: `${delay}ms` } : ENTRANCE_STYLE
  }

  const chipsFor = (msg: ChatRoomMessageDto, align: 'start' | 'end') => {
    const forMessage = reactionsBySequence.get(msg.sequenceIndex)
    if (!forMessage || forMessage.length === 0) {
      return null
    }
    return (
      <div className={align === 'start' ? 'pl-9' : undefined}>
        <ReactionChips
          messageId={msg.id}
          reactions={forMessage}
          participants={participants}
          liveReactionKeys={liveReactionKeys}
          align={align}
        />
      </div>
    )
  }

  // Twins first, the assistant last: its chart closes the round, so it types last too.
  const typingSpeakers: Array<TypingSpeaker> = [
    ...thinkingTwinIds.map((id) => {
      const participant = participants.find((p) => p.id === id)
      return {
        id,
        name: participant?.displayName ?? 'Twin',
        imageUrl: participant?.imageUrl ?? null,
        kind: 'twin' as const,
      }
    }),
    ...(isGeorgeThinking
      ? [{ id: GEORGE_PARTICIPANT_ID, name: assistantName, imageUrl: null, kind: 'assistant' as const }]
      : []),
  ]

  return (
    <>
      {messages.map((msg, msgIndex) => {
        const showDividerBefore = guestDividerIndex != null && msgIndex === guestDividerIndex
        const rowKey = renderKeys.get(msg) ?? msg.id
        const isNew = newMessageIds.has(rowKey)

        const divider = showDividerBefore ? <GuestDivider /> : null

        if (msg.senderType === 'system') {
          return (
            <React.Fragment key={rowKey}>
              {divider}
              <div
                className="flex justify-center py-2"
                style={isNew ? ENTRANCE_STYLE : undefined}
                data-testid={`message-${msg.id}`}
              >
                <span className="text-xs text-twin-chat-fg-muted bg-twin-chat-bubble/30 px-3 py-1 rounded-full">
                  {msg.content}
                </span>
              </div>
            </React.Fragment>
          )
        }

        if (msg.senderType === 'user') {
          return (
            <React.Fragment key={rowKey}>
              {divider}
              <div className="flex flex-col gap-2" style={entranceStyle(msg)} data-testid={`message-${msg.id}`}>
                <DocumentChips msg={msg} />
                <ChatBubble role="user" content={userTextWithoutDocuments(msg.content, msg.documentRefs)} />
                <UserMessageImages msg={msg} roomToken={roomToken} getImageUrl={getImageUrl} />
                {chipsFor(msg, 'end')}
              </div>
            </React.Fragment>
          )
        }

        const isGeorge = msg.senderType === 'george'
        const participant = findParticipant(participants, msg.senderParticipantId)
        const name = isGeorge ? assistantName : (participant?.displayName ?? 'Twin')
        const rawTagline = isGeorge ? null : (participant?.tagline ?? null)
        const audienceTagNames = !isGeorge ? (participant?.audienceTagNames ?? []) : []
        const tagline = buildFullTagline(rawTagline, audienceTagNames)
        const imageUrl = isGeorge ? null : (participant?.imageUrl ?? null)
        const seed = isGeorge ? GEORGE_PARTICIPANT_ID : (participant?.personaId ?? 'unknown')
        const initials = isGeorge ? '' : getUserInitials(name)

        const sampleArtifact =
          msg.metadataType === 'sample_artifact' && isSampleArtifact(msg.metadata)
            ? msg.metadata
            : null

        const audiencePlot =
          msg.metadataType === 'audience_plot' && isAudiencePlotMetadata(msg.metadata)
            ? msg.metadata
            : null

        // The assistant's name already says what it is, so it carries no badge.
        const existingBadge = !isGeorge && msg.mode === 'sample' ? (
          <Badge variant="muted" className="text-[10px] px-1.5 py-0">
            sample
          </Badge>
        ) : null

        const answerGrounding = isGeorge ? null : readAnswerGrounding(msg.metadata)
        const groundingMeter = answerGrounding ? (
          <AnswerGroundingMeter messageId={msg.id} level={answerGrounding} />
        ) : null


        return (
          <React.Fragment key={rowKey}>
            {divider}
            <div className="flex flex-col gap-2" style={entranceStyle(msg)} data-testid={`message-${msg.id}`}>
              <ChatBubble
                role="assistant"
                content={audiencePlot ? '' : msg.content}
                isGeorge={isGeorge}
                renderedContent={
                  audiencePlot ? (
                    <AudiencePlotMessage messageId={msg.id} metadata={audiencePlot} />
                  ) : undefined
                }
                avatar={{
                  url: imageUrl,
                  fallbackInitials: initials,
                  seed: isGeorge ? undefined : seed,
                }}
                senderInfo={{
                  name,
                  tagline,
                  badge: existingBadge ?? undefined,
                }}
                belowAvatar={groundingMeter ?? undefined}
                quote={
                  isGeorge ? undefined : (
                    <ReplyQuote metadata={msg.metadata} messages={messages} participants={participants} />
                  )
                }
                extraContent={
                  sampleArtifact ? <SampleArtifactInline metadata={sampleArtifact} /> : undefined
                }
              />
              {chipsFor(msg, 'start')}
            </div>
          </React.Fragment>
        )
      })}

      {typingSpeakers.length > 0 && <TypingGroupRow key="thinking-group" twins={typingSpeakers} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Exported component
// ---------------------------------------------------------------------------

export function MessageList(props: MessageListProps) {
  const {
    messages,
    participants,
    thinkingParticipantIds = [],
    emptyStateText,
    guestDividerIndex,
    reactions = EMPTY_REACTIONS,
    liveReactionKeys,
  } = props
  const { id: routeRoomToken } = useParams<{ id: string }>()
  const roomToken = props.roomToken ?? routeRoomToken
  const getImageUrl = props.getImageUrl ?? chatRoomsApi.getImageUrl

  const isInterviewMode = participants.length === 1

  const { messages: shownMessages, keys: renderKeys } = useMemo(() => toDisplayMessages(messages), [messages])

  // Track new messages — anything beyond the initial count animates in.
  const initialCountRef = useRef(shownMessages.length)
  const newMessageIds = useMemo(() => {
    const ids = new Set<string>()
    for (let i = initialCountRef.current; i < shownMessages.length; i++) {
      ids.add(renderKeys.get(shownMessages[i]) ?? shownMessages[i].id)
    }
    return ids
  }, [shownMessages, renderKeys])

  // Messages landing in the same burst enter in order, 80ms apart (SPEC 4).
  const entranceDelaysRef = useRef(new Map<string, number>())
  const entranceDelays = useMemo(() => {
    const delays = entranceDelaysRef.current
    let burstIndex = 0
    for (const id of newMessageIds) {
      if (!delays.has(id)) {
        delays.set(id, burstIndex * BURST_STAGGER_MS)
        burstIndex += 1
      }
    }
    return delays
  }, [newMessageIds])

  const isEmpty = messages.length === 0 && thinkingParticipantIds.length === 0

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-twin-chat-fg-muted">
          {emptyStateText ?? 'Start the conversation by typing a message below.'}
        </p>
      </div>
    )
  }

  if (isInterviewMode) {
    return (
      <InterviewMessages
        messages={shownMessages}
        renderKeys={renderKeys}
        participant={participants[0] ?? null}
        thinkingParticipantIds={thinkingParticipantIds}
        newMessageIds={newMessageIds}
        guestDividerIndex={guestDividerIndex}
        roomToken={roomToken}
        getImageUrl={getImageUrl}
      />
    )
  }

  return (
    <GroupMessages
      messages={shownMessages}
      renderKeys={renderKeys}
      participants={participants}
      thinkingParticipantIds={thinkingParticipantIds}
      newMessageIds={newMessageIds}
      guestDividerIndex={guestDividerIndex}
      roomToken={roomToken}
      getImageUrl={getImageUrl}
      reactions={reactions}
      liveReactionKeys={liveReactionKeys}
      entranceDelays={entranceDelays}
    />
  )
}
